import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const IDENTIFIER_TYPE_OPTIONS = [
  "SKU",
  "ASIN",
  "EAN",
  "GTIN",
  "UPC",
  "Supplier Code",
] as const;

export interface CreateProductDialogResult {
  identifierType: string;
  primaryValue: string | null;
  description: string | null;
}

interface CreateProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extractedIdentifier: string;
  extractedDescription: string | null;
  orgProductIdentifier: string;
  onConfirm: (result: CreateProductDialogResult) => void;
  isCreating: boolean;
}

export function CreateProductDialog({
  open,
  onOpenChange,
  extractedIdentifier,
  extractedDescription,
  orgProductIdentifier,
  onConfirm,
  isCreating,
}: CreateProductDialogProps) {
  const [identifierType, setIdentifierType] = useState(orgProductIdentifier);
  const [primaryValue, setPrimaryValue] = useState("");
  const [description, setDescription] = useState(extractedDescription ?? "");

  // Reset state when dialog opens with new data
  useEffect(() => {
    if (open) {
      setIdentifierType(orgProductIdentifier);
      setPrimaryValue("");
      setDescription(extractedDescription ?? "");
    }
  }, [open, orgProductIdentifier, extractedDescription]);

  const typeMatchesOrg = identifierType === orgProductIdentifier;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm({
      identifierType,
      primaryValue: typeMatchesOrg ? null : primaryValue.trim() || null,
      description: description.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isCreating) onOpenChange(v); }}>
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => { if (isCreating) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isCreating) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Create New Product</DialogTitle>
          <DialogDescription>
            Creating a product for identifier{" "}
            <span className="font-mono font-semibold">{extractedIdentifier}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="identifier-type">
              What type of identifier is this?
            </Label>
            <Select value={identifierType} onValueChange={setIdentifierType}>
              <SelectTrigger id="identifier-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {IDENTIFIER_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                    {opt === orgProductIdentifier ? " (primary)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!typeMatchesOrg && (
            <div className="space-y-2">
              <Label htmlFor="primary-value">
                {orgProductIdentifier} for this product{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                id="primary-value"
                placeholder={`Enter ${orgProductIdentifier} if known`}
                value={primaryValue}
                onChange={(e) => setPrimaryValue(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                If left blank, the product will be created with the extracted
                identifier and can be updated later.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              placeholder="Product description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Product
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
